import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faLightbulb, faRocket, faCog, faChartLine, faShieldAlt, faTools, faGraduationCap } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import API_CONFIG from './config';
import './styles/PromptGuideModal.css';

const PromptGuideModal = ({ isPromptGuideOpen, handleClosePromptGuide, handleStartConversation, currentBotType = "knowledge" }) => {
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [flippedCards, setFlippedCards] = useState(new Set());

  // Default prompts as fallback with icons and descriptions
  const defaultPrompts = [
    {
      id: "default-1",
      prompt: "How would you design an integrated instrumentation and monitoring plan (IMP) for a deep urban excavation near heritage structures to ensure safety and integrity, including sensor types, data frequency, thresholds, and anomaly response?",
      prompt_title: "Urban Excavation Monitoring",
      prompt_description: "Comprehensive monitoring strategies for urban excavations near sensitive structures",
      icon: faShieldAlt,
      category: "Monitoring"
    },
    {
      id: "default-2",
      prompt: "What are the key differences between ASTM D6230 and EN ISO 18674-3 for inclinometer casing installation, and how is cross-jurisdictional compliance ensured?",
      prompt_title: "Inclinometer Standards",
      prompt_description: "International standards comparison and compliance strategies for inclinometer installations",
      icon: faCog,
      category: "Standards"
    },
    {
      id: "default-3",
      prompt: "What sensor combination, placement, and data interpretation strategy would monitor short-term construction effects and long-term settlement for a high-rise building?",
      prompt_title: "High-rise Monitoring",
      prompt_description: "Advanced monitoring techniques for high-rise construction and settlement analysis",
      icon: faChartLine,
      category: "Construction"
    },
    {
      id: "default-4",
      prompt: "How do you analyze slope stability using both traditional limit equilibrium methods and modern numerical modeling approaches?",
      prompt_title: "Slope Stability Analysis",
      prompt_description: "Comprehensive slope stability assessment using multiple analytical approaches",
      icon: faTools,
      category: "Analysis"
    },
    {
      id: "default-5",
      prompt: "What are the best practices for foundation design in seismic zones with liquefaction potential?",
      prompt_title: "Seismic Foundation Design",
      prompt_description: "Foundation design considerations for earthquake-prone areas with soil liquefaction",
      icon: faGraduationCap,
      category: "Design"
    },
    {
      id: "default-6",
      prompt: "How would you implement a real-time monitoring system for a large dam with multiple failure modes?",
      prompt_title: "Dam Monitoring System",
      prompt_description: "Comprehensive monitoring system design for large dam structures",
      icon: faRocket,
      category: "Infrastructure"
    }
  ];

  // Icon mapping for different categories
  const getIconForCategory = (category) => {
    const iconMap = {
      'Monitoring': faShieldAlt,
      'Standards': faCog,
      'Construction': faChartLine,
      'Analysis': faTools,
      'Design': faGraduationCap,
      'Infrastructure': faRocket,
      'default': faLightbulb
    };
    return iconMap[category] || iconMap.default;
  };

  useEffect(() => {
    const fetchPrompts = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API_CONFIG.BASE_URL}${API_CONFIG.AUTH.PROMPTS}`);
        console.log('API Response:', response.data);
        
        // Handle the updated API response structure
        if (response.data && response.data.prompts && Array.isArray(response.data.prompts)) {
          // Add icons and descriptions to API prompts if not present
          const enhancedPrompts = response.data.prompts.map(prompt => ({
            ...prompt,
            icon: prompt.icon || getIconForCategory(prompt.category || 'default'),
            prompt_description: prompt.prompt_description || "Click to start a conversation about this topic"
          }));
          setPrompts(enhancedPrompts);
        } else if (response.data && Array.isArray(response.data.prompts) && response.data.prompts.length === 0) {
          setPrompts([]);
        } else {
          console.error('Invalid response structure:', response.data);
          setError('Invalid response structure from API');
        }
      } catch (err) {
        console.error('Failed to fetch prompts:', err);
        console.error('Error details:', err.response?.data || err.message);
        console.log('Using default prompts as fallback');
        setPrompts(defaultPrompts);
        setError(null);
      } finally {
        setLoading(false);
      }
    };

    if (isPromptGuideOpen) {
      fetchPrompts();
    }
  }, [isPromptGuideOpen, currentBotType]);

  const handleCardFlip = (cardId) => {
    setFlippedCards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(cardId)) {
        newSet.delete(cardId);
      } else {
        newSet.add(cardId);
      }
      return newSet;
    });
  };

  const handleCardClick = (prompt) => {
    // Send only the prompt title to the backend
    // The backend will check if it's a prompt title and get the full prompt for AI processing
    handleStartConversation(prompt.prompt_title || 'Untitled Prompt');
  };

  if (!isPromptGuideOpen) return null;

  if (loading) {
    return (
      <div className="prompt-modal-overlay">
        <div className="prompt-modal-content">
          <div className="prompt-loading">
            <div className="loading-spinner"></div>
            <p>Loading prompts...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="prompt-modal-overlay">
        <div className="prompt-modal-content">
          <div className="prompt-error">
            <FontAwesomeIcon icon={faTimes} />
            <p>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="prompt-modal-overlay">
      <div className="prompt-modal-content">
        <div className="prompt-modal-header">
          <div className="header-content">
            <h2>
              {currentBotType === 'support' ? 'Support Prompts' : 'Prompt Guide'}
            </h2>
            <p className="desktop-hint">Hover over cards to see descriptions, click to start conversations</p>
            <p className="mobile-hint">Hold over cards to see descriptions, click to start conversations</p>
          </div>
          <button className="prompt-modal-close" onClick={handleClosePromptGuide}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="prompt-cards-grid">
          {prompts.map((prompt, index) => (
            <div
              key={prompt.id}
              className={`prompt-card ${flippedCards.has(prompt.id) ? 'flipped' : ''}`}
              style={{ '--animation-order': index }}
              onMouseEnter={() => handleCardFlip(prompt.id)}
              onMouseLeave={() => handleCardFlip(prompt.id)}
              onClick={() => handleCardClick(prompt)}
            >
              <div className="card-inner">
                {/* Front of card */}
                <div className="card-front">
                  <div className="card-icon">
                    <FontAwesomeIcon icon={prompt.icon} />
                  </div>
                  <h3 className="card-title">{prompt.prompt_title || 'Untitled Prompt'}</h3>
                  {/* <div className="card-category">{prompt.category || 'General'}</div> */}
                  <div className="hover-hint desktop-hint-text">Hover for details</div>
                  <div className="hover-hint mobile-hint-text">Hold for details</div>
                </div>
                
                {/* Back of card */}
                <div className="card-back">
                  <div className="card-description">
                    <h4>Description</h4>
                    <p>{prompt.prompt_description || "Click to start a conversation about this topic"}</p>
                  </div>
                  <div className="card-action">
                    <span>Click to start</span>
                    <FontAwesomeIcon icon={faRocket} />
                  </div>
                </div>
              </div>
            </div>
          ))}
          
          {prompts.length === 0 && !loading && !error && (
            <div className="no-prompts-message">
              <FontAwesomeIcon icon={faLightbulb} />
              <p>No prompts available for this bot type.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PromptGuideModal;